import { type ReactNode, forwardRef, useId } from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import { cn } from "@/lib/cn";

export interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  /** Optional label rendered to the right of the toggle. */
  label?: ReactNode;
  /** Optional helper text under the label. */
  description?: ReactNode;
  /** Wrapper className (the row); pass control styling via `thumbClassName` etc. */
  containerClassName?: string;
}

/**
 * Switch — on/off toggle with optional label (DESIGN-SYSTEM §5).
 * Built on @radix-ui/react-switch. Replaces native checkbox-as-toggle and
 * hand-rolled <button> toggles (maintenance, inbox-enable, unread-only, ...).
 */
export const Switch = forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  SwitchProps
>(({ className, containerClassName, label, description, id, ...props }, ref) => {
  const generatedId = useId();
  const switchId = id ?? generatedId;

  const control = (
    <SwitchPrimitive.Root
      ref={ref}
      id={switchId}
      className={cn(
        "peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border border-transparent",
        "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary",
        "disabled:cursor-not-allowed disabled:opacity-50",
        "data-[state=checked]:bg-accent data-[state=unchecked]:bg-bg-elevated",
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          "pointer-events-none block h-4 w-4 rounded-full bg-fg-on-accent shadow-sm ring-0 transition-transform",
          "data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0.5",
        )}
      />
    </SwitchPrimitive.Root>
  );

  if (!label && !description) return control;

  return (
    <div className={cn("flex items-start gap-3", containerClassName)}>
      {control}
      <div className="space-y-0.5">
        {label && (
          <label
            htmlFor={switchId}
            className="cursor-pointer select-none text-sm font-medium leading-none text-fg-primary peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {label}
          </label>
        )}
        {description && <p className="text-xs text-fg-muted">{description}</p>}
      </div>
    </div>
  );
});
Switch.displayName = "Switch";
